import { createRoot } from "react-dom/client"
import App from "./App.tsx"
import "./index.css"
import { AuthProvider } from "./auth/AuthContext.tsx"
import { Provider as JotaiProvider, createStore } from "jotai"
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { toast } from "sonner"
import { missingScopesFrom } from "./lib/scopeErrors"
import { ENVIRONMENT, SENTRY_DSN, BUILD_VERSION } from "./lib/config"
import { setupProductionConsole } from "./utils/logger"
import { scrubBreadcrumb } from "./lib/sentryScrub"
import * as Sentry from "@sentry/react";
import { ThemeProvider } from "next-themes"

// Before Sentry.init, not after: Sentry wraps whatever console it finds. Run
// after, this put back the unwrapped warn/error and swapped log/info/debug for
// no-ops, so production sent no console breadcrumbs at all. Run first, the
// browser console stays quiet and Sentry still records every call.
setupProductionConsole()

// Only initialize Sentry if not on localhost
if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: BUILD_VERSION || 'unknown',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Email verification and OAuth land here with access_token/refresh_token in
    // the query string. Console crumbs are kept for context and scrubbed as a
    // backstop; the log lines themselves avoid PII. A throw drops the crumb.
    beforeBreadcrumb(breadcrumb) {
      try {
        return scrubBreadcrumb(breadcrumb)
      } catch {
        return null
      }
    },
  });
}

// Handle email verification on app startup
const handleEmailVerification = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const access_token = urlParams.get("access_token")
  const refresh_token = urlParams.get("refresh_token")
  const type = urlParams.get("type")

  if (type === "signup" && access_token && refresh_token) {
    /*
      .then(({ error }) => {
        if (!error) {
          // Clear URL parameters after successful verification
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          )
        }
      })
    */
  }
}

// Call verification handler before rendering
handleEmailVerification()

/**
 * Turn a scope 403 into something readable.
 *
 * The backend answers a missing scope with "Missing required scope(s): x:write".
 * Without this, a read-only user clicking a write control sees nothing happen —
 * indistinguishable from a bug. Catching it centrally means every mutation in
 * the panel explains itself, whether or not its button knows about scopes.
 */
const mutationCache = new MutationCache({
  onError: (error) => {
    const missing = missingScopesFrom(error)
    if (!missing) return
    toast.error("You don't have permission to do that", {
      description: `This action needs: ${missing}`,
    })
  },
})

// Create QueryClient with optimized defaults
const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      // Retry once, but never on a 4xx: retrying a 429 turns one rate-limited
      // page render into a request storm that keeps itself rate-limited.
      retry: (failureCount, error) => {
        const status = (error as { status?: number })?.status
        if (typeof status === "number" && status >= 400 && status < 500) {
          return false
        }
        return failureCount < 1
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  },
})

const store = createStore()


createRoot(document.getElementById("root")!).render(
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
    <JotaiProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </QueryClientProvider>
    </JotaiProvider>
  </ThemeProvider>,
)
